/**
 * BALANCE SHEET — every tunable number in the game lives here.
 *
 * Logan: this is your file. Change a cost or reward, run `npm run sim` to see how the
 * pacing shifts, and `npm test` to make sure nothing broke. No other file needs to change.
 */

export const REWARDS = {
  /** Points for stepping onto an integer you have never visited before. */
  newInteger: 1,
  /** Points for discovering a famous number (on top of the newInteger point). */
  famousNumber: 10,
} as const;

export type UpgradeId =
  'radar' | 'inverter' | 'horizon' | 'dividend' | 'resonator' | 'stride' | 'autowalk';

export type UpgradeGroup = 'explore' | 'primes' | 'speed';

export interface UpgradeDef {
  id: UpgradeId;
  group: UpgradeGroup;
  name: string;
  /** One line explaining what the upgrade is for. */
  blurb: string;
  /** Cost of each level; the array length is the max level. */
  costs: readonly number[];
  /** Effect text shown for a given (1-based) level. */
  effect: (level: number) => string;
  /** Other upgrades (at a minimum level) that must be owned first. */
  requires?: ReadonlyArray<{ id: UpgradeId; level: number }>;
  /**
   * Horizon level needed before buying each level (index 0 = level 1). This is what keeps
   * the economy from soft-locking: the balance test proves every Horizon tier earns enough
   * to buy everything it unlocks, including the next Horizon.
   */
  minHorizon?: readonly number[];
}

/** Highest positive integer reachable at each horizon level (index = level). */
export const HORIZON_CEILINGS = [100, 1_000, 10_000, 100_000] as const;
/** Points per newly discovered prime, by dividend level. */
export const DIVIDEND_BONUS = [0, 2, 5, 12] as const;
/** Points for completing a twin-prime pair, by resonator level. */
export const RESONATOR_BONUS = [0, 10, 40] as const;
/** Integers walked per tap, by stride level. */
export const STRIDE_STEPS = [1, 2, 3, 4, 5] as const;
/** Integers walked per second automatically, by auto-walk level. */
export const AUTOWALK_RATE = [0, 0.5, 1, 2, 4, 8] as const;

const fmt = (n: number) => n.toLocaleString('en-US');

export const UPGRADES: readonly UpgradeDef[] = [
  {
    id: 'radar',
    group: 'primes',
    name: 'Prime Radar',
    blurb: 'A live number line that marks primes before you reach them.',
    costs: [30],
    effect: () => 'Shows the number line with upcoming primes',
  },
  {
    id: 'horizon',
    group: 'explore',
    name: 'Horizon',
    blurb: 'Push the edge of the number line further out.',
    costs: [150, 1_200, 12_000],
    minHorizon: [0, 1, 2],
    effect: (lv) => `Walk up to ${fmt(HORIZON_CEILINGS[lv] ?? 0)}`,
  },
  {
    id: 'inverter',
    group: 'explore',
    name: 'Inverter',
    blurb: 'Flip direction and walk below zero. Fresh ground, fresh points.',
    costs: [80],
    effect: () => 'Walk into negative integers',
  },
  {
    id: 'dividend',
    group: 'primes',
    name: 'Prime Dividend',
    blurb: 'Every new prime medal pays out extra points.',
    costs: [60, 600, 6_000],
    effect: (lv) => `+${DIVIDEND_BONUS[lv] ?? 0} points per new prime`,
    requires: [{ id: 'radar', level: 1 }],
    minHorizon: [0, 2, 3],
  },
  {
    id: 'resonator',
    group: 'primes',
    name: 'Twin Resonator',
    blurb: 'Completing a twin-prime pair (like 11 & 13) rings out a bonus.',
    costs: [120, 2_400],
    effect: (lv) => `+${RESONATOR_BONUS[lv] ?? 0} points per twin pair`,
    requires: [{ id: 'radar', level: 1 }],
    minHorizon: [1, 2],
  },
  {
    id: 'stride',
    group: 'speed',
    name: 'Stride',
    blurb: 'Each tap covers more ground. Every integer still counts.',
    costs: [200, 1_500, 9_000, 40_000],
    minHorizon: [1, 2, 3, 3],
    effect: (lv) => `${STRIDE_STEPS[lv] ?? 1} steps per tap`,
  },
  {
    id: 'autowalk',
    group: 'speed',
    name: 'Auto-Walk',
    blurb: 'Keeps walking while you watch. Stops at the horizon.',
    costs: [250, 1_200, 5_000, 20_000, 60_000],
    minHorizon: [1, 2, 3, 3, 3],
    effect: (lv) => `${AUTOWALK_RATE[lv] ?? 0} steps per second`,
  },
];

export const UPGRADE_BY_ID: Readonly<Record<UpgradeId, UpgradeDef>> = Object.fromEntries(
  UPGRADES.map((u) => [u.id, u]),
) as Record<UpgradeId, UpgradeDef>;

export const GROUP_LABELS: Readonly<Record<UpgradeGroup, string>> = {
  explore: 'Explore',
  primes: 'Primes',
  speed: 'Speed',
};

/** Speed-trial lengths in seconds. */
export const TRIAL_LENGTHS = [10, 20, 30] as const;
export type TrialLength = (typeof TRIAL_LENGTHS)[number];
